// Choose… on a Wise history transfer: what it paid, picked by hand when no suggestion is right. The vendor comes from
// the transfer (its linked Wise recipient, else the payee's name) or is picked; its open bills, PO parts (deposit,
// balance, no price yet), run costs and tooling are listed with what each owes, each ticked with an amount (part of
// one is fine). The totals say what's left over: nothing → Link; within $1 → the vendor rounded; the transfer is more
// → what HHL paid extra (a bank charge, shipping, other: a run cost when something picked is on a run); it's less →
// pay part of something. shared/bills.ts planLink() checks it all here as you go, and the server checks it again.
import { AlertTriangle } from "lucide-react";
import { useMemo, useState } from "react";
import {
  ADJUSTMENT_LABELS,
  type Choice,
  choosable,
  type MatchAdjustment,
  type MatchTargetKind,
  planLink,
  priceText,
  ROUNDING_MAX,
  roundingText,
  transferLike,
  transferVendor,
  vendorLabelOf,
} from "../../../../shared/bills.ts";
import { numberOf } from "../../../../shared/calc.ts";
import type { WiseTransfer } from "../../../../shared/db.gen.ts";
import { amount, dayOf, postJson, useBillIndex } from "../../lib/bills.ts";
import { mutateWithResult } from "../../lib/mutate.ts";
import { Badge, Button, cx, Input, Select } from "../../ui/controls.tsx";
import { type Column, DataTable } from "../../ui/DataTable.tsx";
import { toast } from "../../ui/toast.tsx";
import { Dialog } from "./PayDialogs.tsx";

const KIND_LABELS: Record<MatchTargetKind, string> = {
  bill: "Bill",
  po: "PO",
  po_unpriced: "PO, no price yet",
  production_cost: "Run cost",
  tooling_item: "Tooling",
};
type Extra = "bank_charge" | "shipping" | "other";
// A table row carries its key as its `id` (and `key`), so a cell asking for a row's key gets that one, not a key of it
// (rows lost the choice's own id, which made "It paid" read $0.00 and a row's note miss what was ticked).
const keyOf = (c: Choice & { key?: string }) => c.key ?? `${c.kind}:${c.id}:${c.part ?? ""}`;
const cents = (n: number) => Math.round(n * 100);
const num = (s: string) => numberOf(s) || 0;

export function ChooseDialog({
  t,
  onClose,
  vendorId: startVendor,
  prepick,
}: {
  t: WiseTransfer;
  onClose: () => void;
  /** Find its Wise transfer (WisePicker): the vendor of what's being paid, and that run cost or bill ticked to start. */
  vendorId?: string;
  prepick?: { kind: MatchTargetKind; id: string };
}) {
  const ix = useBillIndex();
  const tl = useMemo(() => transferLike(t), [t]);
  const went = useMemo(() => transferVendor(ix, tl), [ix, tl]);
  const [vendorId, setVendorId] = useState(startVendor || went?.vendorId || "");
  // Ticked rows and the amount typed for each (what the transfer paid of it).
  // A charge recorded as paid by this transfer starts ticked (an unpriced PO is then priced from what's left), and so
  // does what the transfer was picked for (WisePicker).
  const isPrepick = (c: Choice) => !!prepick && c.kind === prepick.kind && c.id === prepick.id;
  const pretick = (vid: string) =>
    new Map(
      (vid ? choosable(ix, vid, dayOf, tl) : [])
        .filter((c) => (c.paidByThis || isPrepick(c)) && !c.blocked && !c.unavailable && c.due != null)
        .map((c) => [keyOf(c), (c.due as number).toFixed(2)] as const),
    );
  const [picked, setPicked] = useState(() => pretick(startVendor || went?.vendorId || ""));
  const [extraKind, setExtraKind] = useState<Extra>("bank_charge");
  const [extraText, setExtraText] = useState("");
  const [extraRun, setExtraRun] = useState("");
  const [paymentId] = useState(() => crypto.randomUUID());
  const [busy, setBusy] = useState(false);
  // Table views --- the choices' View and Columns menus, on the vendor's row
  const [slot, setSlot] = useState<HTMLDivElement | null>(null);
  const cur = tl.currency;
  // With the transfer: what it can't pay (paid by card, another transfer, another currency) is shown, not selectable.
  const choices = useMemo(() => (vendorId ? choosable(ix, vendorId, dayOf, tl) : []), [ix, vendorId, tl]);
  const vendorOptions = useMemo(
    () => [
      { value: "", label: "Pick the vendor…" },
      ...ix.d.vendors
        .filter((v) => !v.archived || v.id === vendorId)
        .map((v) => ({ value: v.id, label: vendorLabelOf(v) }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    ],
    [ix, vendorId],
  );
  const chosen = choices.filter((c) => picked.has(keyOf(c)));
  const amountOf = (c: Choice) => num(picked.get(keyOf(c)) ?? "");
  const targets = chosen.map((c) => ({ kind: c.kind, id: c.id, ...(c.part ? { part: c.part } : {}), amount: amountOf(c) }));
  const pickedCents = chosen.reduce((n, c) => n + cents(amountOf(c)), 0);
  /** + the transfer is more than what's picked. */
  const diff = (cents(tl.amount) - pickedCents) / 100;
  const runs = [...new Set(chosen.map((c) => c.runId).filter((r): r is string => !!r))];
  const runId = extraRun === "none" ? null : extraRun && runs.includes(extraRun) ? extraRun : (runs[0] ?? null);
  const mode = !chosen.length ? "none" : cents(diff) === 0 ? "exact" : Math.abs(cents(diff)) <= cents(ROUNDING_MAX) ? "rounding" : diff > 0 ? "extra" : "over";
  const adjustments: MatchAdjustment[] =
    mode === "rounding"
      ? [{ kind: "rounding", amount: diff, description: roundingText(diff, cur) }]
      : mode === "extra"
        ? [{ kind: extraKind, amount: diff, description: extraText.trim() || ADJUSTMENT_LABELS[extraKind], run_id: runId }]
        : [];
  const plan = chosen.length ? planLink(ix, tl, { targets, adjustments }, dayOf) : null;
  // Picked more than the transfer: that's said below (pay part of something), not as a problem.
  const problems = (plan?.problems ?? []).filter((p) => mode !== "over" || !p.startsWith("What you picked adds up"));
  const roundsOn = plan?.lines.findIndex((ls) => ls.some((l) => l.adjustment != null && l.kind === "rounding")) ?? -1;
  const largest = [...chosen].filter((c) => c.due != null).sort((a, b) => amountOf(b) - amountOf(a))[0];
  const partOf = largest && mode === "over" ? Math.round((amountOf(largest) + diff) * 100) / 100 : 0;

  const toggle = (c: Choice, on: boolean) => {
    const next = new Map(picked);
    if (!on) next.delete(keyOf(c));
    else {
      // What it owes; a PO without a price takes what the transfer leaves.
      const left = (cents(tl.amount) - pickedCents) / 100;
      next.set(keyOf(c), (c.due ?? Math.max(left, c.priced)).toFixed(2));
    }
    setPicked(next);
  };
  /** A row's click (or Enter): tick it, or untick it; what it can't pay can't be ticked. */
  const flip = (c: Choice) => {
    const on = picked.has(keyOf(c));
    if (!on && (c.blocked ?? c.unavailable)) return;
    toggle(c, !on);
  };
  /** The ticks (one, a shift-click range or all): each newly ticked fills in what it owes, as ticking it alone does. */
  const tick = (next: Set<string>) => {
    const out = new Map([...picked].filter(([k]) => next.has(k)));
    let pickedNow = choices.filter((c) => out.has(keyOf(c))).reduce((n, c) => n + cents(num(out.get(keyOf(c)) ?? "")), 0);
    for (const c of choices) {
      const k = keyOf(c);
      if (!next.has(k) || out.has(k) || c.blocked || c.unavailable) continue;
      const value = c.due ?? Math.max((cents(tl.amount) - pickedNow) / 100, c.priced);
      out.set(k, value.toFixed(2));
      pickedNow += cents(value);
    }
    setPicked(out);
  };
  const rows = choices.map((c, order) => ({ ...c, id: keyOf(c), key: keyOf(c), order }));
  const noteOf = (c: Choice) => {
    const on = picked.has(keyOf(c));
    const off = c.blocked ?? c.unavailable;
    // A PO without its price: the price this payment would set, so the person sees it before linking.
    const price = on ? plan?.prices[chosen.findIndex((x) => keyOf(x) === keyOf(c))] : null;
    const amt = amountOf(c);
    const keeps = on && c.due != null && cents(amt) < cents(c.due) ? c.due - amt : 0;
    const over = on && c.due != null && cents(amt) > cents(c.due);
    const text =
      off ??
      (over
        ? `More than the ${amount(c.due ?? 0, cur)} it owes`
        : price
          ? `Sets its price: ${priceText(price.price, cur)} a unit (${amount(price.forLine, cur)} ÷ ${price.qty.toLocaleString("en-US")})`
          : keeps
            ? `Keeps ${amount(keeps, cur)} to pay`
            : [
                c.invoice && `Invoice ${c.invoice}`,
                c.date,
                c.namesTransfer ? "recorded as paid by this transfer" : "",
                c.kind === "po_unpriced" ? "its price is set from what you pay" : "",
              ]
                .filter(Boolean)
                .join(" · "));
    return { text, tone: off || over ? "text-warn" : keeps ? "text-accent-text" : "text-muted", over };
  };
  const columns: Column<(typeof rows)[number]>[] = [
    {
      key: "label",
      header: "What",
      width: "minmax(200px,2fr)",
      cell: (c) => (
        <span className={cx("flex min-w-0 items-center gap-1.5", (c.blocked ?? c.unavailable) && "opacity-60")}>
          <Badge>{c.part === "deposit" ? "Deposit" : KIND_LABELS[c.kind]}</Badge>
          <span className="truncate">{c.label}</span>
        </span>
      ),
    },
    {
      key: "note",
      header: "Note",
      width: "minmax(160px,1.5fr)",
      value: (c) => noteOf(c).text,
      cell: (c) => {
        const n = noteOf(c);
        return (
          <span className={cx("truncate text-[12px]", n.tone)} title={n.text}>
            {n.text}
          </span>
        );
      },
    },
    {
      key: "due",
      header: "Owes",
      width: "110px",
      align: "right",
      value: (c) => c.due ?? "",
      text: (c) => (c.due == null ? "any amount" : amount(c.due, cur)),
      cell: (c) => <span className="font-mono text-[12px] text-muted">{c.due == null ? "any amount" : amount(c.due, cur)}</span>,
    },
    {
      key: "pays",
      header: "It paid",
      headerTitle: "What the transfer paid of it: click to change (part of one is fine)",
      width: "120px",
      align: "right",
      value: (c) => (picked.has(c.id) ? amountOf(c) : ""),
      text: (c) => picked.get(c.id) ?? "",
      cell: (c) =>
        picked.has(c.id) ? (
          <span className={cx("font-mono text-[12px]", noteOf(c).over && "text-warn")}>{amount(amountOf(c), cur)}</span>
        ) : (
          <span className="text-faint">—</span>
        ),
      edit: {
        get: (c) => picked.get(c.id) ?? "",
        save: (c, text) => setPicked(new Map(picked).set(c.id, text)),
        can: (c) => picked.has(c.id),
      },
    },
    { key: "order", header: "#", width: "48px", align: "right", optional: true, cell: (c) => <span className="text-faint">{c.order + 1}</span> },
  ];
  const link = async () => {
    if (!plan || problems.length || mode === "over" || mode === "none") return;
    setBusy(true);
    const { ok, result } = await mutateWithResult("wise_transfer.link", { id: t.id, payment_id: paymentId, targets, adjustments });
    // With a token, Wise's own receipt is fetched for it.
    if (ok && t.source === "api") await postJson(`/api/wise/payments/${paymentId}/receipt`).catch(() => {});
    setBusy(false);
    if (!ok) return;
    const r = (result ?? {}) as { bills?: string[]; keeps?: string[]; fee?: string; priced?: string[]; costs?: string[] };
    const notes = [
      ...(r.priced ?? []),
      ...(r.keeps ?? []),
      ...(r.costs ?? []).map((c) => `run cost added, ${c}`),
      ...(r.fee && r.fee !== "added to the run" ? [r.fee] : []),
    ];
    toast.ok(`Transfer ${t.wise_transfer_id} linked to ${(r.bills ?? []).join(", ")}${notes.length ? `. ${notes.join("; ")}` : ""}`);
    onClose();
  };

  return (
    <Dialog
      title={`What did transfer ${t.wise_transfer_id} pay?`}
      sub={
        <>
          {t.payee_name || "Unknown payee"} · <span className="font-mono">{amount(tl.amount, cur)}</span> · {t.reference || "(no reference)"}
          {tl.transfer_date ? ` · ${tl.transfer_date}` : ""}
        </>
      }
      onClose={onClose}
      width={780}
    >
      <div className="mt-3 flex items-center gap-2 text-[12px]">
        <span className="text-muted">Vendor</span>
        <Select
          value={vendorId}
          options={vendorOptions}
          onChange={(e) => {
            setVendorId(e.target.value);
            setPicked(pretick(e.target.value));
          }}
          className="h-8 w-72"
          aria-label="Vendor"
        />
        <span className="whitespace-nowrap text-muted">
          {went && went.vendorId === vendorId ? (went.how === "recipient" ? "from its Wise recipient" : "from the payee's name") : "who it paid"}
        </span>
        <div ref={setSlot} className="ml-auto flex items-center gap-1" />
      </div>
      <div className="mt-3">
        <DataTable
          fit
          rows={rows}
          columns={columns}
          editing
          selection={{ selected: new Set(picked.keys()), onChange: tick }}
          onOpen={flip}
          defaultSort={{ key: "order" }}
          exportName="What a transfer paid"
          viewsSlot={slot}
          pageFilters={false}
          rowNoun={["choice", "choices"]}
          empty={
            <div className="p-4 text-center text-[12px] text-muted">
              {vendorId ? "Nothing of theirs is open here: no PO, bill, run cost or tooling to pay (paid before HHL Ops?)." : "Pick the vendor it went to."}
            </div>
          }
        />
      </div>

      <div className="mt-3 flex flex-wrap items-center justify-end gap-x-4 gap-y-1 text-[12px] text-muted">
        <span>
          Picked <span className="font-mono text-text">{amount(pickedCents / 100, cur)}</span>
        </span>
        <span>
          Transfer <span className="font-mono text-text">{amount(tl.amount, cur)}</span>
        </span>
        <span>
          Difference{" "}
          <span className={cx("font-mono", mode === "exact" ? "text-ok" : mode === "none" ? "text-text" : "text-warn")}>
            {mode === "exact" ? "none" : mode === "none" ? amount(diff, cur) : `${amount(Math.abs(diff), cur)} ${diff > 0 ? "more paid" : "less paid"}`}
          </span>
        </span>
      </div>

      {mode === "rounding" && (
        <div className="mt-2 rounded-md bg-surface-2/60 px-3 py-2 text-[12px]">
          {roundingText(diff, cur)}: it goes on {roundsOn >= 0 ? chosen[roundsOn]?.label : "the largest"}'s bill as a rounding line, so that's paid in full.
        </div>
      )}
      {mode === "extra" && (
        <div className="mt-2 grid gap-2 rounded-md bg-surface-2/60 px-3 py-2 text-[12px]">
          <div>
            HHL paid <span className="font-mono">{amount(diff, cur)}</span> more than what's picked. Add it as:
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Select
              value={extraKind}
              options={(["bank_charge", "shipping", "other"] as const).map((k) => ({ value: k, label: ADJUSTMENT_LABELS[k] }))}
              onChange={(e) => setExtraKind(e.target.value as Extra)}
              className="h-7 w-36"
              aria-label="What the extra was"
            />
            <Input value={extraText} onChange={(e) => setExtraText(e.target.value)} placeholder={ADJUSTMENT_LABELS[extraKind]} className="h-7 w-56" />
            {runs.length > 0 && (
              <Select
                value={runId ?? "none"}
                options={[
                  ...runs.map((r) => ({ value: r, label: `A run cost on ${ix.runById.get(r)?.number ?? "the run"}` })),
                  { value: "none", label: "A charge on the bill only" },
                ]}
                onChange={(e) => setExtraRun(e.target.value)}
                className="h-7 w-56"
                aria-label="Where the extra goes"
              />
            )}
          </div>
        </div>
      )}
      {mode === "over" && (
        <div className="mt-2 flex flex-wrap items-center gap-2 rounded-md bg-surface-2/60 px-3 py-2 text-[12px]">
          What's picked is {amount(-diff, cur)} more than the transfer: lower an amount to pay part of something.
          {largest && partOf > 0 && (
            <Button size="sm" onClick={() => setPicked(new Map(picked).set(keyOf(largest), partOf.toFixed(2)))}>
              Pay {amount(partOf, cur)} of {largest.label}
            </Button>
          )}
        </div>
      )}
      {problems.length > 0 && (
        <div className="mt-2 grid gap-1 text-[12px] text-warn">
          {problems.map((p) => (
            <div key={p} className="flex items-start gap-1.5">
              <AlertTriangle className="mt-0.5 size-3.5 shrink-0" /> {p}
            </div>
          ))}
        </div>
      )}
      {plan && !problems.length && plan.keeps.length > 0 && mode !== "over" && (
        <div className="mt-2 text-[12px] text-muted">{plan.keeps.map((k) => `${k.label} keeps ${amount(k.left, cur)} to pay`).join(" · ")}</div>
      )}

      <div className="mt-4 flex justify-end gap-2">
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="primary" disabled={busy || !plan || problems.length > 0 || mode === "over" || mode === "none"} onClick={link}>
          {mode === "rounding"
            ? "Vendor rounded: link"
            : mode === "extra"
              ? `Add ${amount(diff, cur)} as ${(extraText.trim() || ADJUSTMENT_LABELS[extraKind]).toLowerCase()} and link`
              : "Link"}
        </Button>
      </div>
    </Dialog>
  );
}
